// Kinio KMS 操作台 M2 的共用小元件：主機狀態標記、全域關閉紅條、網路區「讀不到」橫條、時段編輯器、現場資訊卡、〔監控設定〕入口。
// 畫面語彙照 SPEC #kinio-kms-ui 與設計系統：每個狀態都是圖示 + 文字；預期離線 ☾ 與更新中 ⟳ 是灰色、不是離線；讀不到 ⊘ 虛線框、不准綠色。
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScope } from '../../lib/scope';
import { stamp, timeStamp } from '../../lib/timefmt';
import { useKmgrDashboard } from './hooks';
import { DEFAULT_BUSINESS_WINDOW, hostPill, minutesSince, type HostPill as HostPillKind, type ProbeState } from './model';
import { NS } from './parts';
import type { CollectorOffline, GlobalStop, HostRow, SiteInfo, TimeWindow } from './types';

const HOST_ICON: Record<HostPillKind, string> = { online: '✓', offline: '✕', expected: '☾', updating: '⟳', suspended: '✕', revoked: '○', unknown: '⊘' };

/** 主機狀態標記：✓ 在線 / ✕ 離線 2 小時 / ☾ 已關機 · 18:42 / ☾ 不在現場 · 2 天 / ⟳ 更新中 · 4 分鐘 / ✕ 已停用（複製）/ ⊘ 不知道；本機已暫停另寫小字。 */
export function HostStatePill({ h, now = Date.now() }: { h: HostRow; now?: number }) {
  const { t } = useTranslation(NS);
  const { pill, detail } = hostPill(h);
  let text: string;
  const since = h.last_seen_at ? sinceText(t, h.last_seen_at, now) : '';
  switch (pill) {
    case 'online':
      text = t('host.online');
      break;
    case 'offline':
      text = since ? t('host.offline_for', { for: since }) : t('host.offline');
      break;
    case 'expected':
      text = detail === 'shutdown' ? t('host.shutdown', { time: h.last_seen_at ? timeStamp(h.last_seen_at, undefined, false) : '' }) : detail === 'sleep' ? t('host.sleep') : t('host.away', { for: since });
      break;
    case 'updating':
      text = t('host.updating', { n: minutesSince(h.last_seen_at, now) });
      break;
    case 'suspended':
      text = t('host.suspended');
      break;
    case 'revoked':
      text = t('host.revoked');
      break;
    default:
      text = t('host.unknown');
  }
  return (
    <span className={'km-st ' + pill} data-host-state={pill}>
      <span aria-hidden>{HOST_ICON[pill]}</span>
      {text}
    </span>
  );
}

/** 「2 小時」「3 天」「12 分鐘」 */
export function sinceText(t: (k: string, o?: Record<string, unknown>) => string, iso: string, now = Date.now()): string {
  const m = minutesSince(iso, now);
  if (m >= 2 * 1440) return t('dur.days', { n: Math.floor(m / 1440) });
  if (m >= 90) return t('dur.hours', { n: Math.floor(m / 60) });
  return t('dur.minutes', { n: m });
}

/** 未授權標記（SPEC #kinio-kms-license：只回報在線與基本資訊，不存指標、不發告警、不能遠端）。 */
export function UnlicensedMark() {
  const { t } = useTranslation(NS);
  return (
    <span className="km-st unlicensed" data-license="unlicensed">
      <span aria-hidden>!</span>
      {t('host.unlicensed')}
    </span>
  );
}

const PROBE_ICON: Record<ProbeState, string> = { ok: '✓', bad: '!', unassigned: '○', noresult: '⟳', unreadable: '⊘' };
const PROBE_CLASS: Record<ProbeState, string> = { ok: 'ok', bad: 'attention', unassigned: 'unassigned', noresult: 'noresult', unreadable: 'unreadable' };

/** 探針 / 網路設備狀態：✓ 可以讀取 / ! 沒有回應 · 驗證失敗 / ○ 沒有收集器負責 / ⟳ 還沒有結果 / ⊘ 讀不到（收集器離線，結果是舊的）。 */
export function ProbeStatePill({ state, text }: { state: ProbeState; text: string }) {
  return (
    <span className={'km-st ' + PROBE_CLASS[state]} data-probe-state={state}>
      <span aria-hidden>{PROBE_ICON[state]}</span>
      {text}
    </span>
  );
}

/** 操作台頁頂紅條：「Kinio 已暫停所有自動處置與遠端協助（〈時間〉起）：〈原因〉」，沒有〔恢復〕鈕（SPEC #kinio-kms-global-stop）。 */
export function GlobalStopBar({ stop }: { stop: GlobalStop | undefined }) {
  const { t } = useTranslation(NS);
  if (!stop?.active) return null;
  return (
    <div className="km-bar bad global-stop" role="alert" data-testid="km-global-stop">
      <span className="ic" aria-hidden>
        ✕
      </span>
      <span className="txt">
        <strong>{t('global_stop.title', { time: stop.started_at ? stamp(stop.started_at) : '' })}</strong>
        {stop.reason ? <>：{stop.reason}</> : null}
        <br />
        <span className="km-sub">{t(stop.scope === 'org' ? 'global_stop.scope_org' : 'global_stop.scope_all')}</span>
      </span>
    </div>
  );
}

/** 用範圍「全部」的 dashboard 畫紅條（IT 監控各頁與總覽共用；dashboard 已經在輪詢，不另打）。 */
export function KmgrGlobalStopBar() {
  const { data } = useKmgrDashboard('all');
  return <GlobalStopBar stop={data?.global_stop} />;
}

/** 收集器離線的「讀不到」橫條：「讀不到網路設備（收集器〈名稱〉離線 12 分鐘）· 交換器、AP 都沒算進數字〔查看收集器〕」（UX-04、KMS-25）。 */
export function NetUnreadableBar({ offline, reason, homeId, compact }: { offline: CollectorOffline[]; reason?: 'collector_offline' | 'snmp_auth_failed'; homeId?: string; compact?: boolean }) {
  const { t } = useTranslation(NS);
  const first = offline[0];
  const who = first ? (first.name || t('net.collector_unnamed')) : '';
  const text =
    reason === 'snmp_auth_failed'
      ? t('net.unreadable_auth')
      : first
        ? t('net.unreadable_collector', { name: who, for: first.last_seen_at ? sinceText(t, first.last_seen_at) : '' }) + (offline.length > 1 ? t('net.and_more', { n: offline.length - 1 }) : '')
        : t('net.unreadable');
  return (
    <div className="km-bar unreadable" role="status" data-testid="km-net-unreadable">
      <span className="ic" aria-hidden>
        ⊘
      </span>
      <span className="txt">
        {text}
        {!compact && <span className="muted"> · {t('net.unreadable_note')}</span>}
      </span>
      <Link className="btn btn-sm" to={homeId ? `/kmgr/network?tab=collectors&home=${encodeURIComponent(homeId)}` : '/kmgr/network?tab=collectors'}>
        {t('net.see_collectors')}
      </Link>
    </div>
  );
}

/** 一週時段的編輯器（維護排程、營業時段、主機預期離線共用）：每列星期晶片 + 開始 / 結束；至少一天。 */
export function WindowsEditor({ value, onChange, addDefault = DEFAULT_BUSINESS_WINDOW, label }: { value: TimeWindow[]; onChange(v: TimeWindow[]): void; addDefault?: TimeWindow; label: string }) {
  const { t } = useTranslation(NS);
  const set = (i: number, patch: Partial<TimeWindow>) => onChange(value.map((w, k) => (k === i ? { ...w, ...patch } : w)));
  return (
    <div data-testid="km-windows">
      <ul className="km-windows" aria-label={label}>
        {value.map((w, i) => (
          <li key={i} className="km-window">
            <span className="days" role="group" aria-label={t('window.days')}>
              {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                <label key={d}>
                  <input type="checkbox" checked={w.days.includes(d)} onChange={(e) => set(i, { days: e.target.checked ? [...w.days, d].sort() : w.days.filter((x) => x !== d) })} />
                  {t(`day.${d}`)}
                </label>
              ))}
            </span>
            <label>
              <span className="km-sr">{t('window.start')}</span>
              <input className="input" type="time" value={w.start} onChange={(e) => set(i, { start: e.target.value })} />
            </label>
            <span aria-hidden>–</span>
            <label>
              <span className="km-sr">{t('window.end')}</span>
              <input className="input" type="time" value={w.end} onChange={(e) => set(i, { end: e.target.value })} />
            </label>
            <button type="button" className="btn btn-sm" onClick={() => onChange(value.filter((_, k) => k !== i))}>
              {t('window.remove')}
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className="btn btn-sm" onClick={() => onChange([...value, { ...addDefault }])}>
        {t('window.add')}
      </button>
      <p className="muted km-sub" style={{ marginTop: 6 }}>
        {t('window.hint')}
      </p>
    </div>
  );
}

/**
 * 現場資訊卡（事故頁第一塊之後；資料在家戶監控設定「現場資訊」）：客戶組織、服務等級與到期日（寫明不影響通知）、現場聯絡人〔撥打〕、IT 支援、機櫃、門禁。
 * 桌面可收合並記住偏好（localStorage）；沒有任何欄位時不畫。
 */
export function SiteInfoCard({ info, orgName, homeId, collapsible = true }: { info: SiteInfo | undefined; orgName?: string; homeId?: string; collapsible?: boolean }) {
  const { t } = useTranslation(NS);
  const [open, setOpen] = useState(() => readPref());
  if (!info || !hasSiteInfo(info)) return null;
  const toggle = () => {
    setOpen(!open);
    try {
      localStorage.setItem(SITE_PREF, open ? '0' : '1');
    } catch {
      /* 無 storage */
    }
  };
  return (
    <section className="card km-site" data-testid="km-site" aria-labelledby="km-site-h">
      <div className="km-site-head">
        <h2 id="km-site-h">{t('site.title')}</h2>
        {homeId && (
          <Link className="km-sub" to={`/sites/kmgr/${encodeURIComponent(homeId)}/site`}>
            {t('site.edit')}
          </Link>
        )}
        <span className="spacer" />
        {collapsible && (
          <button type="button" className="btn btn-sm" aria-expanded={open} onClick={toggle}>
            {open ? t('site.collapse') : t('site.expand')}
          </button>
        )}
      </div>
      {open && (
        <dl className="km-dl" style={{ marginTop: 8 }}>
          {(orgName || info.service_level) && (
            <Row label={t('site.customer')}>
              {[orgName, info.service_level ? t('site.level_n', { level: info.service_level }) : ''].filter(Boolean).join(' · ')}
              {info.contract_until ? `（${t('site.until', { date: info.contract_until })}）` : ''}
              <span className="sub muted km-sub" style={{ display: 'block' }}>
                {t('site.level_note')}
              </span>
            </Row>
          )}
          {(info.contact_name || info.contact_phone) && (
            <Row label={t('site.contact')}>
              {[info.contact_role, info.contact_name].filter(Boolean).join(' ')}
              {info.contact_phone && (
                <>
                  {' · '}
                  <span className="phone">{info.contact_phone}</span>{' '}
                  <a className="btn btn-sm" href={'tel:' + info.contact_phone.replace(/[^\d+]/g, '')}>
                    {t('site.call')}
                  </a>
                </>
              )}
            </Row>
          )}
          {info.it_support && <Row label={t('site.it_support')}>{info.it_support}</Row>}
          {info.rack_note && <Row label={t('site.rack')}>{info.rack_note}</Row>}
          {info.access_note && <Row label={t('site.access')}>{info.access_note}</Row>}
        </dl>
      )}
    </section>
  );
}

const SITE_PREF = 'vms.kmgr.site_card_open';
function readPref(): boolean {
  try {
    return localStorage.getItem(SITE_PREF) !== '0';
  } catch {
    return true;
  }
}

export function hasSiteInfo(i: SiteInfo): boolean {
  return !!(i.service_level || i.contact_name || i.contact_phone || i.it_support || i.rack_note || i.access_note);
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

/** IT 監控各頁右上〔監控設定〕：範圍只有一個已啟用家戶 → 直接跳；多個 → 先列家戶（details 選單）。 */
export function SettingsEntry() {
  const { t } = useTranslation(NS);
  const { data } = useKmgrDashboard();
  const { homes } = useScope();
  const items = (data?.homes.items ?? []).map((h) => ({ id: h.home_id, name: h.home_name || homes.find((x) => x.home_id === h.home_id)?.display_name || h.home_id }));
  if (!items.length) return null;
  if (items.length === 1)
    return (
      <Link className="btn btn-sm" to={`/sites/kmgr/${encodeURIComponent(items[0].id)}`} data-testid="km-settings-entry">
        {t('home_settings.title')}
      </Link>
    );
  return (
    <details className="km-menu" data-testid="km-settings-entry">
      <summary className="btn btn-sm">{t('home_settings.title')} ▾</summary>
      <ul className="plain-list card" style={{ position: 'absolute', right: 0, zIndex: 5, minWidth: 200, marginTop: 4 }}>
        {items.map((h) => (
          <li key={h.id}>
            <Link to={`/sites/kmgr/${encodeURIComponent(h.id)}`}>{h.name}</Link>
          </li>
        ))}
      </ul>
    </details>
  );
}
